// Pure model of what an overlay's values.yaml holds per workload, and the
// comparisons the configuration screens make across environments. No React,
// no fetch, no YAML parsing — the backend hands over the parsed document.

/** The base chart's workload keys. `cronjob` is left out: it uses
 * `configMapData` and a different chart, and no Catalyst overlay carries it. */
export const WORKLOAD_KEYS = ['api', 'ep', 'task'] as const;

/** Column order on screen. Environments an overlay does not have are simply
 * absent; anything unknown sorts last, alphabetically. */
export const ENVIRONMENT_ORDER = ['dev', 'staging', 'prod', 'mgmt'] as const;

export function compareEnvironments(a: string, b: string): number {
  const order = ENVIRONMENT_ORDER as readonly string[];
  const left = order.indexOf(a);
  const right = order.indexOf(b);
  if (left === -1 && right === -1) return a.localeCompare(b);
  if (left === -1) return 1;
  if (right === -1) return -1;
  return left - right;
}

export interface ConfigValue {
  /** Always a string: the chart quotes every value at render time, so a bare
   * `50` in the file and a `"50"` are the same ConfigMap entry. */
  value: string;
  /** `block` marks a multi-line value (`|` / `>`). Shown, never edited: a
   * line-level edit cannot safely rewrite a folded scalar. */
  style: 'scalar' | 'block';
}

export interface WorkloadResources {
  requests: { cpu?: string; memory?: string };
  limits: { cpu?: string; memory?: string };
}

export interface WorkloadAutoscaling {
  enabled: boolean;
  minReplicas: number | null;
  maxReplicas: number | null;
}

export interface WorkloadValues {
  environment: string;
  /** The values key — `api`, `ep`, `task`. */
  workload: string;
  /** Overlay path in gitops-platform, so the screen can link to the file. */
  path: string;
  /** `enabled: false` stubs exist (`task` on trip-hq). Nothing is deployed
   * from them and nothing may be written to them. */
  enabled: boolean;
  configMap: Record<string, ConfigValue>;
  /** Names under `secrets:` — environment variables fed from the Secret. The
   * values are not here, and the names are never editable from the portal. */
  secrets: string[];
  /** Ignored by the chart while `autoscaling.enabled` is true. */
  replicaCount: number | null;
  autoscaling: WorkloadAutoscaling | null;
  resources: WorkloadResources;
}

export interface ServiceValues {
  service: string;
  /** Every environment the service has an overlay in, in column order. */
  environments: string[];
  workloads: WorkloadValues[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/** Quantities may be written bare (`cpu: 1`) or as strings (`10m`). Either
 * way the screen shows and compares text. */
function asQuantity(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function configValueOf(value: unknown): ConfigValue | null {
  if (typeof value === 'string') {
    return { value, style: value.includes('\n') ? 'block' : 'scalar' };
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return { value: String(value), style: 'scalar' };
  }
  // `key:` with nothing after it parses as null and renders as an empty
  // string, which is what the chart would emit.
  if (value === null) {
    return { value: '', style: 'scalar' };
  }
  return null;
}

/**
 * Reads every workload section of one parsed overlay document.
 *
 * Tolerant on purpose: a malformed section is skipped rather than turned into
 * `[object Object]`, the same stance `unitsFromValues` takes for image tags.
 */
export function workloadValuesFromDocument(
  document: unknown,
  environment: string,
  path: string,
): WorkloadValues[] {
  const values = asRecord(document);
  if (!values) {
    return [];
  }

  const workloads: WorkloadValues[] = [];
  for (const workload of WORKLOAD_KEYS) {
    const section = asRecord(values[workload]);
    if (!section) {
      continue;
    }

    const configMap: Record<string, ConfigValue> = {};
    for (const [key, raw] of Object.entries(asRecord(section.configMap) ?? {})) {
      const entry = configValueOf(raw);
      if (entry) {
        configMap[key] = entry;
      }
    }

    const secrets = Array.isArray(section.secrets)
      ? section.secrets.filter((s): s is string => typeof s === 'string')
      : [];

    const autoscaling = asRecord(section.autoscaling);
    const resources = asRecord(asRecord(section.deployment)?.resources);
    const requests = asRecord(resources?.requests) ?? {};
    const limits = asRecord(resources?.limits) ?? {};

    workloads.push({
      environment,
      workload,
      path,
      enabled: section.enabled !== false,
      configMap,
      secrets,
      replicaCount: asInteger(section.replicaCount),
      autoscaling: autoscaling
        ? {
            enabled: autoscaling.enabled === true,
            minReplicas: asInteger(autoscaling.minReplicas),
            maxReplicas: asInteger(autoscaling.maxReplicas),
          }
        : null,
      resources: {
        requests: { cpu: asQuantity(requests.cpu), memory: asQuantity(requests.memory) },
        limits: { cpu: asQuantity(limits.cpu), memory: asQuantity(limits.memory) },
      },
    });
  }

  return workloads;
}

export function workloadsOf(values: ServiceValues): string[] {
  const seen = new Set<string>();
  for (const entry of values.workloads) {
    seen.add(entry.workload);
  }
  return (WORKLOAD_KEYS as readonly string[]).filter(w => seen.has(w));
}

export function workloadValuesFor(
  values: ServiceValues,
  environment: string,
  workload: string,
): WorkloadValues | undefined {
  return values.workloads.find(
    entry => entry.environment === environment && entry.workload === workload,
  );
}

/** Environments in which a workload is present and enabled — the columns a
 * key can be missing from. A disabled stub is not a place a key is missing. */
export function environmentsWith(
  values: ServiceValues,
  workload: string,
): string[] {
  return values.environments.filter(environment => {
    const entry = workloadValuesFor(values, environment, workload);
    return Boolean(entry?.enabled);
  });
}

/**
 * Environment naming, masked, so that two values that differ only by where
 * they point compare equal.
 *
 * Nearly every value differs between environments — `-dev` suffixes,
 * `dev-catalyst-…svc.cluster.local` hosts, `preprod` tenants. Highlighting all
 * of them would highlight the whole table and hide the one difference that
 * matters. `Debug` against `Information` still shows, because it should.
 */
const ENVIRONMENT_TOKEN = /\b(development|production|preprod|staging|stage|prod|dev)\b/gi;

export function maskEnvironmentNaming(value: string): string {
  return value.replace(ENVIRONMENT_TOKEN, '<env>');
}

export interface KeyRow {
  key: string;
  perEnvironment: Record<string, ConfigValue | undefined>;
  /** Environments with the workload enabled and this key absent. */
  missingIn: string[];
  /** Any two present values differ at all. */
  differs: boolean;
  /** They still differ once environment naming is masked. */
  differsBeyondNaming: boolean;
}

/**
 * One row per configMap key of a workload, across every environment.
 *
 * Keys keep the order they have in the file, first environment first, so the
 * table reads like the overlay does; keys that appear only in a later
 * environment follow in theirs.
 */
export function configKeyMatrix(
  values: ServiceValues,
  workload: string,
): KeyRow[] {
  const environments = environmentsWith(values, workload);
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const environment of environments) {
    const entry = workloadValuesFor(values, environment, workload);
    for (const key of Object.keys(entry?.configMap ?? {})) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }

  return keys.map(key => {
    const perEnvironment: Record<string, ConfigValue | undefined> = {};
    const missingIn: string[] = [];
    for (const environment of environments) {
      const value = workloadValuesFor(values, environment, workload)?.configMap[key];
      perEnvironment[environment] = value;
      if (!value) {
        missingIn.push(environment);
      }
    }
    const present = environments
      .map(environment => perEnvironment[environment]?.value)
      .filter((value): value is string => value !== undefined);
    const differs = new Set(present).size > 1;
    const differsBeyondNaming =
      new Set(present.map(maskEnvironmentNaming)).size > 1;
    return { key, perEnvironment, missingIn, differs, differsBeyondNaming };
  });
}

/** The scaling fields, addressed the way the form and the editor both do. */
export type ResourceField = 'requests.cpu' | 'requests.memory' | 'limits.memory';

export const RESOURCE_FIELDS: readonly ResourceField[] = [
  'requests.cpu',
  'requests.memory',
  'limits.memory',
];

export function resourceValue(
  resources: WorkloadResources,
  field: ResourceField,
): string | undefined {
  switch (field) {
    case 'requests.cpu':
      return resources.requests.cpu;
    case 'requests.memory':
      return resources.requests.memory;
    case 'limits.memory':
      return resources.limits.memory;
    default:
      return undefined;
  }
}

/**
 * One edit to one overlay, as the form describes it and the action applies it.
 *
 * Addressed by environment and workload rather than by file path: the path is
 * derived on the backend from the same rule the reads use, so the browser
 * never names a file.
 */
export type ConfigChange =
  | {
      environment: string;
      workload: string;
      kind: 'configMap';
      key: string;
      value: string;
    }
  | {
      environment: string;
      workload: string;
      kind: 'replicaCount';
      value: number;
    }
  | {
      environment: string;
      workload: string;
      kind: 'resource';
      field: ResourceField;
      value: string;
    };

/** Where in the document a change lands, from the workload key down. */
export function changePath(change: ConfigChange): string[] {
  switch (change.kind) {
    case 'configMap':
      return [change.workload, 'configMap', change.key];
    case 'replicaCount':
      return [change.workload, 'replicaCount'];
    case 'resource':
      return [change.workload, 'deployment', 'resources', ...change.field.split('.')];
    default:
      return [];
  }
}

function isConfigValue(value: unknown): value is ConfigValue {
  const entry = asRecord(value);
  return (
    Boolean(entry) &&
    typeof entry!.value === 'string' &&
    (entry!.style === 'scalar' || entry!.style === 'block')
  );
}

function isWorkloadValues(value: unknown): value is WorkloadValues {
  const entry = asRecord(value);
  if (!entry) return false;
  const configMap = asRecord(entry.configMap);
  return (
    typeof entry.environment === 'string' &&
    typeof entry.workload === 'string' &&
    typeof entry.path === 'string' &&
    typeof entry.enabled === 'boolean' &&
    Boolean(configMap) &&
    Object.values(configMap!).every(isConfigValue) &&
    Array.isArray(entry.secrets) &&
    (entry.replicaCount === null || typeof entry.replicaCount === 'number') &&
    Boolean(asRecord(entry.resources))
  );
}

export function isServiceValuesResponse(value: unknown): value is ServiceValues {
  const body = asRecord(value);
  return (
    Boolean(body) &&
    typeof body!.service === 'string' &&
    Array.isArray(body!.environments) &&
    body!.environments.every(e => typeof e === 'string') &&
    Array.isArray(body!.workloads) &&
    body!.workloads.every(isWorkloadValues)
  );
}
